import { FormFields } from "$root/ui-lib/form/FormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircularProgressBar, Dialog, DialogFooter, type DialogProps, Icon } from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import type { Set } from "immutable";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import type { InstrumentEntities } from "../../const";
import { EntityEditorControllerApiFactory } from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import { axiosExtract } from "$root/third-party-integrations/axios";

export type AddMissingInstrumentDialogProps = {
	instrumentInComposition: Set<string>;
	defaultIdentifier?: string;
	onSubmit(data: { identifier: string; weight: number }): void;
	onAnimationStateChange?: DialogProps["onAnimationStateChange"];
	entity: InstrumentEntities;
} & MinimumDialogProps;

const defaultInstrumentValue = { identifier: "", weight: 0.01 };
function AddMissingInstrumentDialog(props: AddMissingInstrumentDialogProps) {
	const { t } = useTranslation();

	const editorApi = useApiGen(EntityEditorControllerApiFactory);
	const { checkingNameUniqueness: checkingTickerValidity, checkIfNameIsAvailable: checkTickerValidity } =
		useDebouncedNameUniquenessChecker({
			isNameAvailableApi: (identifier) => axiosExtract(editorApi.validateTicker(identifier)),
			currentName: "",
		});

	const { formState, control, handleSubmit } = useForm({
		defaultValues: props.defaultIdentifier
			? { ...defaultInstrumentValue, identifier: props.defaultIdentifier }
			: defaultInstrumentValue,
		resolver: zodResolver(
			z.object(
				props.entity === "UNIVERSE"
					? {
							identifier: z
								.string()
								.min(1)
								.refine((identifier) => !props.instrumentInComposition.has(identifier), {
									message: "identifier is duplicated",
								})
								.refine((identifier) => checkTickerValidity(identifier), { message: "Identifier not valid" }),
						}
					: {
							identifier: z
								.string()
								.min(1)
								.refine((identifier) => dbg(!props.instrumentInComposition.has(identifier)), {
									message: "identifier is duplicated",
								})
								.refine((identifier) => checkTickerValidity(identifier), { message: "Identifier not valid" }),

							weight: z.number().min(0.01).max(100),
						},
			),
		),
	});

	return (
		<Dialog
			size="medium"
			show={props.show}
			onClose={props.onClose}
			onAnimationStateChange={props.onAnimationStateChange}
			onSubmitAsync={() => handleSubmit(props.onSubmit)()}
			header="Add instrument"
			footer={
				<DialogFooter
					primaryAction={<SubmitButton>{t("BUTTON.ADD")}</SubmitButton>}
					neutralAction={
						<Button palette="tertiary" onClick={props.onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<FormFields.Text
				control={control}
				formState={formState}
				label="Identifier"
				name="identifier"
				rightContent={
					checkingTickerValidity ? <CircularProgressBar className="w-3" value="indeterminate" /> : undefined
				}
			/>
			{props.entity !== "UNIVERSE" && (
				<>
					<br />
					<FormFields.NullableNumber
						control={control}
						formState={formState}
						label="Weight"
						name="weight"
						rightContent={<Icon icon="Percentile" />}
					/>
				</>
			)}
		</Dialog>
	);
}

type SpawnAddMissingInstrumentDialogProps = Omit<AddMissingInstrumentDialogProps, "show" | "onClose">;
export function spawnInstrumentDialog(props: SpawnAddMissingInstrumentDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<AddMissingInstrumentDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmit={(instruments) => {
					props.onSubmit(instruments);
					resolve();
				}}
			/>
		)),
	);
}
